import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/CriticalAnalysisIntensity/criticalAnalysisIntensity.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Switch } from 'components/DesignSystem/Switch';
import { LoaderContainer } from 'components/common';
import { useTrendsLogic } from 'components/widgets/Trends/Trends.logic';
import { useTrendsProvider } from 'components/widgets/Trends/Trends.provider';
import { Filters } from 'components/widgets/Trends/components/Filters/Filters';
import { TrendsGraph } from 'components/widgets/Trends/components/TrendsGraph/TrendsGraph';

export interface Dataset {
    title: string;
    seriesname: string;
    data: {
        text: string;
        displayValue: string;
        label: number;
        value: number;
        actualValue: number;
    }[];
    color: string;
    selectedByDefault: boolean;
}

export interface GraphData {
    dataset: Dataset[];
}

const { componentKey, id, title: defaultTitle, endpoint } = widgetsConfigurations.trends;

export const Trends = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = defaultTitle,
}: IComponentProps) => {
    const { ui, api } = useTrendsProvider({
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        globalFilterData,
        localFilters,
        widgetViewMode,
        componentKey,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.crossVersionsTrends.payload,
        widgetEndpoint: endpoint,
    });

    const { logic } = useTrendsLogic({
        ui,
        api,
        widgetId,
        widgetViewMode,
    });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleComponents={[
                    WidgetViewModeUtils.isPageOrReportAddOrEditWidget(widgetViewMode) && (
                        <div data-testid={'annotation-filter'} className={classes.annotationFilter}>
                            <Switch
                                onChange={() => ui.setters.setToggleLabels(!ui.state.toggleLabels)}
                                checked={ui.state.toggleLabels}
                                label={ui.state.toggleLabels ? 'Hide Value Labels' : 'Show Value Labels'}
                                labelPlacement={'start'}
                            />
                        </div>
                    ),
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        shouldHide={true}
                        shouldUseNewIcon={true}
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        apiPayload={api.crossVersionsTrends.payload}
                        widgetEndpoint={endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown key={'export'} title={title} subTitle={subTitle} />
                    ),
                    <TooltipIcon
                        key={'info'}
                        tooltip={
                            ui.state.singleLine === 'Single Parameter (Actual value)'
                                ? api.crossVersionsTrends.instance.data?.actual?.info
                                : api.crossVersionsTrends.instance.data?.normalized?.info
                        }
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                        />
                    ),
                ]}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={WidgetViewModeUtils.isPinboardView(widgetViewMode)}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    WidgetViewModeUtils.isPageOrReportAddOrEditWidget(widgetViewMode) && (
                        <Filters
                            key={'filters'}
                            options={logic.options}
                            handleSelectOptions={logic.handleSelectOptions}
                            handleSingle={logic.handleSingle}
                            sourceData={ui.state}
                        />
                    ),
                ]}
            >
                {api.crossVersionsTrends.instance.isLoading && !api.crossVersionsTrends.instance.data && (
                    <LoaderContainer>
                        <OverlayWithSpinner />
                    </LoaderContainer>
                )}
                {!api.crossVersionsTrends.instance.isLoading &&
                    api.crossVersionsTrends.instance.data &&
                    ui.state.selectedOptions && (
                        <TrendsGraph
                            data={logic.graphData}
                            toggleLabels={ui.state.toggleLabels}
                            chartHeight={logic.chartHeight}
                        />
                    )}
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
